// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material"
import {
  Phone,
  Mail,
  Edit,
  Building,
  MapPin,
  User,
  Smartphone,
  FileText,
  Calendar,
  CheckCircle,
  XCircle,
  Factory,
} from "lucide-react"
import type { Contact } from "@crm-src/lib/contacts-api"
import { formatIndustry } from "@crm-src/lib/industries"

interface ViewContactDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contact: Contact | null
  onEdit: (contact: Contact) => void
}

export function ViewContactDialog({
  open,
  onOpenChange,
  contact,
  onEdit,
}: ViewContactDialogProps) {
  const iconSx = { color: "text.secondary", display: "inline-flex", flexShrink: 0, mt: "2px" }

  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: "1.5rem" }}>Contact Details</DialogTitle>

      <DialogContent>
        {contact && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 2 }}>
            {/* Header with name and status */}
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <Box>
                <Typography component="h3" sx={{ fontSize: "1.25rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                  <Box component="span" sx={{ color: "text.secondary", display: "inline-flex" }}>
                    <User size={20} />
                  </Box>
                  {contact.full_name || contact.email || `Contact #${contact.id}`}
                </Typography>
                {contact.job_title && (
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                    {contact.job_title}
                    {contact.company && ` at ${contact.company}`}
                  </Typography>
                )}
              </Box>
              <Chip
                size="small"
                color={contact.active ? "success" : "default"}
                icon={contact.active ? <CheckCircle size={12} /> : <XCircle size={12} />}
                label={contact.active ? "Active" : "Inactive"}
                sx={{ ml: 1 }}
              />
            </Box>

            {/* Contact Information Section */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography component="h4" sx={{ fontWeight: 600, fontSize: "0.875rem", color: "text.secondary", mb: 1.5 }}>CONTACT INFORMATION</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                  {contact.email && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Box component="span" sx={iconSx}><Mail size={16} /></Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Email</Typography>
                        <Link href={`mailto:${contact.email}`} sx={{ fontSize: "0.875rem", color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                          {contact.email}
                        </Link>
                      </Box>
                    </Box>
                  )}

                  {contact.phone && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Box component="span" sx={iconSx}><Phone size={16} /></Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Phone</Typography>
                        <Link href={`tel:${contact.phone}`} sx={{ fontSize: "0.875rem", color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                          {contact.phone}
                        </Link>
                      </Box>
                    </Box>
                  )}

                  {contact.mobile && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Box component="span" sx={iconSx}><Smartphone size={16} /></Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Mobile</Typography>
                        <Link href={`tel:${contact.mobile}`} sx={{ fontSize: "0.875rem", color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                          {contact.mobile}
                        </Link>
                      </Box>
                    </Box>
                  )}

                  {contact.company && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Box component="span" sx={iconSx}><Building size={16} /></Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Company</Typography>
                        <Typography variant="body2">{contact.company}</Typography>
                      </Box>
                    </Box>
                  )}

                  {contact.industry && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Box component="span" sx={iconSx}><Factory size={16} /></Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Industry</Typography>
                        <Typography variant="body2">{formatIndustry(contact.industry)}</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Address Section */}
              <Box>
                <Typography component="h4" sx={{ fontWeight: 600, fontSize: "0.875rem", color: "text.secondary", mb: 1.5 }}>ADDRESS</Typography>
                {(contact.address?.trim() || contact.city?.trim() || contact.state?.trim() || contact.postal_code?.trim() || contact.country?.trim()) ? (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box component="span" sx={iconSx}><MapPin size={16} /></Box>
                    <Box sx={{ fontSize: "0.875rem", display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {contact.address && <Typography variant="body2">{contact.address}</Typography>}
                      {(contact.city || contact.state || contact.postal_code) && (
                        <Typography variant="body2">
                          {[contact.city, contact.state, contact.postal_code]
                            .filter(v => v && v.trim())
                            .join(', ')}
                        </Typography>
                      )}
                      {contact.country && <Typography variant="body2">{contact.country}</Typography>}
                      {!contact.address && !contact.city && !contact.state && !contact.postal_code && !contact.country && (
                        <Typography variant="body2" sx={{ color: "text.disabled", fontStyle: "italic" }}>No address information</Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 2, fontSize: "0.875rem", color: "text.secondary" }}>
                    Address not provided
                  </Box>
                )}
              </Box>

              {/* Notes Section */}
              {contact.notes && (
                <Box>
                  <Typography component="h4" sx={{ fontWeight: 600, fontSize: "0.875rem", color: "text.secondary", mb: 1.5 }}>NOTES</Typography>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box component="span" sx={iconSx}><FileText size={16} /></Box>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{contact.notes}</Typography>
                  </Box>
                </Box>
              )}

              {/* Metadata */}
              <Box sx={{ pt: 2, borderTop: 1, borderColor: "divider" }}>
                <Typography component="h4" sx={{ fontWeight: 600, fontSize: "0.875rem", color: "text.secondary", mb: 1.5 }}>ADDITIONAL INFORMATION</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, fontSize: "0.875rem" }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box component="span" sx={iconSx}><Calendar size={16} /></Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Created</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {new Date(contact.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box component="span" sx={iconSx}><Calendar size={16} /></Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Last Updated</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {new Date(contact.updated_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Box>

                  {contact.user && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Box component="span" sx={iconSx}><User size={16} /></Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Created By</Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {contact.user.full_name || contact.user.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={() => onOpenChange(false)}>
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<Edit size={16} />}
          onClick={() => {
            onOpenChange(false)
            if (contact) onEdit(contact)
          }}
        >
          Edit Contact
        </Button>
      </DialogActions>
    </Dialog>
  )
}
